import { useCallback, useContext, useEffect, useRef } from 'react';
import type { FlatListProps } from 'react-native';
import { bridge, isNotNil } from 'shared/src/utils';
import type { GoodsData } from '@banhala/ui-kit';
import type { CodiGoodsItem } from 'shared/src/api';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { GoodsParamsContext } from '../pages/GoodsDetail/context/GoodsParamsContext';
import {
  retainedStateFamily,
  useRetainedRef,
} from 'shared/src/hooks/retainedState';

type GoodsItem = GoodsData & CodiGoodsItem;

// carousel(dedupKey)별 DISPLAY 로깅 index — remount 재발화(중복 과금) 방지.
const loggedIndicesFamily = retainedStateFamily<string, number[]>(() => []);

export const useLogDisplayGoods = (options?: {
  enabled?: boolean;
  dedupKey?: string;
}) => {
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // 찜탭 C군(EXP1768)처럼 route 밖에서 시트로 띄울 때는 GoodsParamsContext 가 주입되어
  // SCREEN 을 대신 채운다. PDP 내 carousel 은 injected 가 없어 GOODS_DETAIL 유지.
  const injected = useContext(GoodsParamsContext);
  const screen = injected?.logging?.screen ?? 'GOODS_DETAIL';

  // dedupKey 지정 시: visit-scoped(remount 보존, PDP carousel). 미지정 시(모달 등)는
  // 컴포넌트 로컬 — 매 마운트 새 집계(기존 동작 유지). 훅은 항상 둘 다 호출하고 선택만 분기.
  const localLoggedList = useRef<Array<number>>([]);
  const retainedLoggedList = useRetainedRef(
    loggedIndicesFamily(options?.dedupKey ?? '__component_local__'),
  );
  const goodsItemLoggedList =
    options?.dedupKey != null ? retainedLoggedList : localLoggedList;
  const pendingItems = useRef<Array<{ index: number; item: GoodsItem }>>([]);

  useEffect(() => {
    if (enabled && pendingItems.current.length > 0) {
      pendingItems.current.forEach(({ index, item }) => {
        if (goodsItemLoggedList.current.includes(index)) return;
        goodsItemLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          channel: ['amplitude', 'ablylog', 'firebase'],
          name: 'DISPLAY_GOODS',
          ably_only_log: item.logging.ably_only_log,
          analytics: {
            SCREEN: screen,
            ad: item.item.ad,
            ...item.logging.analytics,
            INDEX: index,
          },
        });
        if (isNotNil(item.item.ad)) {
          bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
        }
      });
      pendingItems.current = [];
    }
  }, [enabled, screen, goodsItemLoggedList]);

  const logDisplayGoods = useCallback<
    NonNullable<FlatListProps<GoodsItem>['onViewableItemsChanged']>
  >(({ viewableItems }) => {
    viewableItems.forEach(({ index, isViewable, item }) => {
      if (index === null || !isViewable) {
        return;
      }
      if (goodsItemLoggedList.current.includes(index)) {
        return;
      }

      if (!enabledRef.current) {
        if (!pendingItems.current.some(p => p.index === index)) {
          pendingItems.current.push({ index, item });
        }
        return;
      }

      goodsItemLoggedList.current.push(index);

      bridge.logAnalyticsEvent({
        channel: ['amplitude', 'ablylog', 'firebase'],
        name: 'DISPLAY_GOODS',
        ably_only_log: item.logging.ably_only_log,
        analytics: {
          SCREEN: screen,
          ad: item.item.ad,
          ...item.logging.analytics,
          INDEX: index,
        },
      });
      if (isNotNil(item.item.ad)) {
        bridge.logAdEvent({ action: AdAction.DISPLAY, ad: item.item.ad });
      }
    });
  }, [screen, goodsItemLoggedList]);

  return { logDisplayGoods };
};
